import { FC, useEffect, useState } from 'react';
import { Card } from 'antd';
import { Line } from '@ant-design/plots';
import { PricePoint } from '../../api/priceSimulation';
import { formatDateTime } from '../../utils/dateFormat';

interface PriceChartProps {
  data: PricePoint[];
  loading?: boolean;
}

// 为了处理时间字段，创建格式化数据接口
interface FormattedPricePoint {
  timePointFormatted: string;
  price: number;
  sequenceNumber: number;
}

const PriceChart: FC<PriceChartProps> = ({ data, loading }) => {
  const [chartData, setChartData] = useState<FormattedPricePoint[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      console.log('PriceChart 接收到的原始数据:', data);
      console.log('数据类型:', typeof data, Array.isArray(data));
      
      // 检查数据是否有嵌套结构
      if (data && typeof data === 'object' && 'data' in data) {
        // 如果是嵌套结构如 { code: 0, message: 'ok', data: [...] }
        console.log('检测到嵌套数据格式:', data);
        const nestedData = (data as any).data;
        if (Array.isArray(nestedData)) {
          const formattedData = nestedData.map(item => ({
            timePointFormatted: formatTimePoint(item.timePoint),
            price: Number(item.price || 0),
            sequenceNumber: Number(item.sequenceNumber || 0)
          }));
          console.log('从嵌套结构中提取并格式化数据:', formattedData);
          setChartData(formattedData);
          return;
        }
      }
      
      // 正常数组处理
      if (data && Array.isArray(data) && data.length > 0) {
        // 处理ISO格式的时间字符串
        const formattedData = data.map((item: PricePoint) => {
          console.log('处理数据项:', item);
          return {
            timePointFormatted: formatTimePoint(item.timePoint),
            price: Number(item.price || 0),
            sequenceNumber: Number(item.sequenceNumber || 0)
          };
        });
        
        // 按照sequenceNumber排序确保图表正确显示
        formattedData.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
        
        console.log('格式化后的数据:', formattedData);
        setChartData(formattedData);
      } else {
        console.log('数据为空或不是数组:', data);
        setChartData([]);
      }
    } catch (err) {
      console.error('数据处理错误:', err);
      setError(err instanceof Error ? err.message : '未知错误');
    }
  }, [data]);

  // 格式化时间点，将ISO字符串转换为更友好的显示格式
  const formatTimePoint = (timePoint: string | number | undefined): string => {
    if (!timePoint) return '0';
    
    if (typeof timePoint === 'string') {
      try {
        // 尝试解析ISO日期字符串
        const date = new Date(timePoint);
        if (!isNaN(date.getTime())) {
          return formatDateTime(date, { showDate: false, showTime: true, showSeconds: true });
        }
      } catch (e) {
        console.error('解析时间出错:', e);
      }
    }
    
    return String(timePoint);
  };

  if (error) {
    return <Card title="错误" style={{ marginTop: 16 }}>处理数据时出错: {error}</Card>;
  }

  if (!chartData || chartData.length === 0) {
    console.log('图表数据为空，不渲染图表');
    return <Card title="价格曲线预览" style={{ marginTop: 16 }} loading={loading} variant="borderless">暂无数据</Card>;
  }

  try {
    console.log('准备渲染图表，数据:', chartData);
    
    // 使用新的配置适应修改后的数据格式
    const config = {
      data: chartData,
      xField: 'timePointFormatted',
      yField: 'price',
      padding: 'auto',
      xAxis: {
        type: 'cat',
        label: {
          autoRotate: true,
          autoHide: true,
        }
      },
      tooltip: {
        showTitle: true,
        title: '时间点',
        formatter: (datum: any) => {
          return {
            name: '价格',
            value: datum.price.toFixed(2)
          };
        },
      },
      legend: {
        position: 'top'
      },
      yAxis: {
        label: {
          formatter: (value: number) => value.toFixed(2)
        }
      }
    };

    return (
      <Card title="价格曲线预览" style={{ marginTop: 16 }} loading={loading} variant="borderless">
        <Line {...config} />
      </Card>
    );
  } catch (err) {
    console.error('渲染图表错误:', err);
    return <Card title="错误" style={{ marginTop: 16 }}>渲染图表时出错: {err instanceof Error ? err.message : '未知错误'}</Card>;
  }
};

export default PriceChart; 